class CactusProductEditorial extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.inventories = {};
    this.mediaButtons = [];
    this.mediaPanels = [];
    this.activeMediaIndex = 0;
    this.sizeGuideOpener = null;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    const variantJson = this.querySelector('[data-variant-json]');
    const inventoryJson = this.querySelector('[data-inventory-json]');

    if (variantJson?.textContent) {
      try {
        this.variants = JSON.parse(variantJson.textContent);
      } catch (error) {
        console.error('Unable to read product variants', error);
      }
    }

    if (inventoryJson?.textContent) {
      try {
        this.inventories = JSON.parse(inventoryJson.textContent);
      } catch (error) {
        console.error('Unable to read product inventory', error);
      }
    }

    this.mediaButtons = Array.from(this.querySelectorAll('[data-media-target]'));
    this.mediaPanels = Array.from(this.querySelectorAll('[data-media-panel]'));

    const { signal } = this.abortController;

    this.mediaButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.showMedia(index), { signal });
    });

    this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.stepMedia(-1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.stepMedia(1), { signal });

    this.querySelectorAll('[data-option-input]').forEach((input) => {
      input.addEventListener('change', () => this.updateVariant(), { signal });
    });

    this.querySelectorAll('[data-accordion-toggle]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const accordion = button.closest('.cep-accordion');
          const contentId = button.getAttribute('aria-controls');
          const content = contentId ? document.getElementById(contentId) : null;
          const isOpen = button.getAttribute('aria-expanded') === 'true';

          button.setAttribute('aria-expanded', String(!isOpen));
          accordion?.classList.toggle('is-open', !isOpen);
          if (content) content.hidden = isOpen;
        },
        { signal }
      );
    });

    this.querySelector('[data-buy-now]')?.addEventListener('click', () => this.buyNow(), { signal });

    const sizeGuideDialog = this.querySelector('[data-size-guide-dialog]');

    this.querySelectorAll('[data-size-guide-open]').forEach((button) => {
      button.addEventListener('click', () => this.openSizeGuide(sizeGuideDialog, button), { signal });
    });

    this.querySelector('[data-size-guide-close]')?.addEventListener(
      'click',
      () => this.closeSizeGuide(sizeGuideDialog),
      { signal }
    );
    sizeGuideDialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === sizeGuideDialog) this.closeSizeGuide(sizeGuideDialog);
      },
      { signal }
    );
    sizeGuideDialog?.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.closeSizeGuide(sizeGuideDialog);
      },
      { signal }
    );
    sizeGuideDialog?.addEventListener('close', () => this.restoreSizeGuideFocus(), { signal });
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !sizeGuideDialog?.open) return;

        event.preventDefault();
        this.closeSizeGuide(sizeGuideDialog);
      },
      { signal }
    );

    document.addEventListener('cart:update', () => this.refreshCartCount(), { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  showMedia(index) {
    if (!this.mediaPanels.length) return;

    const normalizedIndex = (index + this.mediaPanels.length) % this.mediaPanels.length;
    this.activeMediaIndex = normalizedIndex;

    this.mediaPanels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === normalizedIndex;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });

    this.mediaButtons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === normalizedIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });

    const counter = this.querySelector('[data-current-media]');
    if (counter) counter.textContent = String(normalizedIndex + 1);
  }

  stepMedia(direction) {
    this.showMedia(this.activeMediaIndex + direction);
  }

  selectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-position]')).map((group) => {
      return group.querySelector('[data-option-input]:checked')?.value;
    });
  }

  updateVariant() {
    const selectedOptions = this.selectedOptions();
    const variant = this.variants.find((candidate) => {
      return candidate.options.every((value, index) => value === selectedOptions[index]);
    });

    const variantInput = this.querySelector('[data-variant-id]');
    const addButton = this.querySelector('[ref="addToCartButton"]');
    const buyNowButton = this.querySelector('[data-buy-now]');
    const acceleratedCheckout = this.querySelector('[data-accelerated-checkout]');
    const stockStatus = this.querySelector('[data-stock-status]');
    const price = this.querySelector('[data-product-price]');
    const comparePrice = this.querySelector('[data-compare-price]');

    if (!variant) {
      if (variantInput) variantInput.value = '';
      if (addButton) addButton.disabled = true;
      if (buyNowButton) buyNowButton.disabled = true;
      if (acceleratedCheckout) acceleratedCheckout.hidden = true;
      if (stockStatus) {
        stockStatus.classList.remove('is-in-stock', 'is-low-stock');
        stockStatus.classList.add('is-unavailable');
        const message = stockStatus.querySelector('[data-stock-copy]');
        if (message) message.textContent = 'Unavailable';
      }
      this.setAddButtonText('Unavailable');
      return;
    }

    if (variantInput) variantInput.value = variant.id;
    if (addButton) addButton.disabled = !variant.available;
    if (buyNowButton) buyNowButton.disabled = !variant.available;
    if (acceleratedCheckout) acceleratedCheckout.hidden = !variant.available;
    this.setAddButtonText(variant.available ? 'Add to cart' : 'Sold out');

    if (price) price.textContent = this.formatMoney(variant.price);

    if (comparePrice) {
      const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
      comparePrice.hidden = !hasComparePrice;
      comparePrice.textContent = hasComparePrice ? this.formatMoney(variant.compare_at_price) : '';
    }

    if (stockStatus) {
      const message = stockStatus.querySelector('[data-stock-copy]');
      stockStatus.classList.remove('is-in-stock', 'is-low-stock', 'is-unavailable');
      stockStatus.classList.add(this.stockState(variant, message));
      if (message) {
        message.textContent = this.stockCopy(variant, message);
      }
    }

    const featuredMediaId = variant.featured_media?.id;
    if (featuredMediaId) {
      const mediaIndex = this.mediaButtons.findIndex((button) => Number(button.dataset.mediaTarget) === featuredMediaId);
      if (mediaIndex >= 0) this.showMedia(mediaIndex);
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', nextUrl);
  }

  stockState(variant, message) {
    if (!variant?.available) return 'is-unavailable';

    const inventory = this.inventories[String(variant.id)];
    const inventoryQuantity = Number(inventory?.quantity);
    const displayLimit = Number(message?.dataset.inventoryDisplayLimit);
    const showsLowStockCount =
      message?.dataset.showInventoryCount === 'true' &&
      inventory?.management === 'shopify' &&
      inventoryQuantity > 0 &&
      (displayLimit <= 0 || inventoryQuantity <= displayLimit);

    return showsLowStockCount ? 'is-low-stock' : 'is-in-stock';
  }

  stockCopy(variant, message) {
    if (!variant.available) return message.dataset.soldOut || 'Sold out';

    const inventory = this.inventories[String(variant.id)];
    if (
      message.dataset.showInventoryCount === 'true' &&
      inventory?.management === 'shopify' &&
      Number(inventory.quantity) > 0
    ) {
      const inventoryQuantity = Number(inventory.quantity);
      const displayLimit = Number(message.dataset.inventoryDisplayLimit);

      if (displayLimit > 0 && inventoryQuantity > displayLimit) {
        return message.dataset.inStock || 'In stock';
      }

      if (inventoryQuantity === 1) {
        return message.dataset.inventoryCountSingular || 'Only 1 piece left';
      }

      return (message.dataset.inventoryCount || 'Only [quantity] pieces left').replace(
        '[quantity]',
        String(inventoryQuantity)
      );
    }

    return message.dataset.inStock || 'In stock';
  }

  openSizeGuide(dialog, opener) {
    if (!dialog) return;

    this.sizeGuideOpener = opener || document.activeElement;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  closeSizeGuide(dialog) {
    if (!dialog) return;

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      this.restoreSizeGuideFocus();
    }
  }

  restoreSizeGuideFocus() {
    const opener = this.sizeGuideOpener;
    this.sizeGuideOpener = null;

    if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
  }

  setAddButtonText(text) {
    const textElement = this.querySelector('.add-to-cart-text__content');
    if (textElement) textElement.textContent = text;
  }

  formatMoney(cents) {
    const locale = this.dataset.locale || document.documentElement.lang || 'en';
    const currency = this.dataset.currency || 'INR';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  async buyNow() {
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    const button = this.querySelector('[data-buy-now]');

    if (!form || !button || button.disabled || !form.checkValidity()) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!response.ok) throw new Error('Unable to add this item to checkout.');
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch (error) {
      const errorElement = this.querySelector('.cep-form-error');
      if (errorElement) {
        errorElement.classList.remove('hidden');
        errorElement.append(document.createTextNode(error.message));
      }

      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async refreshCartCount() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;
      const cart = await response.json();
      this.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = String(cart.item_count);
      });
    } catch (error) {
      console.error('Unable to refresh cart count', error);
    }
  }
}

if (!customElements.get('cactus-product-editorial')) {
  customElements.define('cactus-product-editorial', CactusProductEditorial);
}
